/**
 * @page /portal/vereinbarungen
 * @description Kunden-Portal: Mutual Commitment Ledger Phase 2 (#931).
 *   4 Tabs: Standard / Aktive / Wartet auf Bestaetigung / Archiv
 * @api GET /api/portal/commitments, /api/portal/commitments/standard-docs
 * @roles portal_kunde
 * @i18n customerPortal.commitments
 */
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { PageHeader } from '../components/ui';
import { FileText, ChevronRight, Clock, CheckCircle2, Archive, BookOpen, Download, Inbox, FileCheck2 } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_CONFIG = {
  active:            { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500',  label: 'customerPortal.commitments.status.active' },
  pending_customer:  { bg: 'bg-amber-500/10',   text: 'text-amber-600 dark:text-amber-400',     dot: 'bg-amber-500',    label: 'customerPortal.commitments.status.pending' },
  rejected_customer: { bg: 'bg-rose-500/10',    text: 'text-rose-600 dark:text-rose-400',       dot: 'bg-rose-500',     label: 'customerPortal.commitments.status.rejected' },
  expired:           { bg: 'bg-gray-500/10',    text: 'text-gray-600 dark:text-gray-400',       dot: 'bg-gray-500',     label: 'customerPortal.commitments.status.expired' },
  superseded:        { bg: 'bg-violet-500/10',  text: 'text-violet-600 dark:text-violet-400',   dot: 'bg-violet-500',   label: 'customerPortal.commitments.status.superseded' },
  terminated:        { bg: 'bg-rose-500/10',    text: 'text-rose-600 dark:text-rose-400',       dot: 'bg-rose-500',     label: 'customerPortal.commitments.status.terminated' },
};

function StatusBadge({ status, t }) {
  const cfg = STATUS_CONFIG[status] || { bg: 'bg-gray-500/10', text: 'text-gray-600', dot: 'bg-gray-500', label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label.startsWith('customerPortal.') ? t(cfg.label) : status}
    </span>
  );
}

function formatDate(v) {
  if (!v) return '\u2013';
  try {
    const s = typeof v === 'string' ? v.slice(0, 10) : v;
    return new Date(typeof s === 'string' ? s + 'T00:00:00' : s).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return String(v); }
}

export default function PortalVereinbarungen() {
  const { t } = useLanguage();
  const [tab, setTab] = useState(null);
  const [items, setItems] = useState([]);
  const [docs, setDocs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/portal/commitments`, { credentials: 'include' }).then(r => r.ok ? r.json() : { commitments: [] }),
      fetch(`${API}/api/portal/commitments/standard-docs`, { credentials: 'include' }).then(r => r.ok ? r.json() : { docs: {} }),
    ]).then(([a, b]) => {
      if (!mounted) return;
      setItems(Array.isArray(a.commitments) ? a.commitments : []);
      setDocs(b.docs || {});
    }).catch(() => {}).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const buckets = useMemo(() => {
    const pending = [], active = [], archive = [];
    for (const c of items) {
      if (c.status === 'pending_customer') pending.push(c);
      else if (c.status === 'active') active.push(c);
      else if (['rejected_customer', 'expired', 'superseded', 'terminated'].includes(c.status)) archive.push(c);
    }
    return { pending, active, archive };
  }, [items]);

  // Dynamic default-tab: pending wenn Action nötig, sonst active als Haupt-Inhalt
  useEffect(() => {
    if (loading || tab !== null) return;
    setTab(buckets.pending.length > 0 ? 'pending' : 'active');
  }, [loading, tab, buckets.pending.length]);

  const tabs = [
    { key: 'pending',  icon: Clock,       label: t('customerPortal.commitments.tabs.pending'),  count: buckets.pending.length, highlight: true },
    { key: 'active',   icon: CheckCircle2,label: t('customerPortal.commitments.tabs.active'),   count: null },
    { key: 'archive',  icon: Archive,     label: t('customerPortal.commitments.tabs.archive'),  count: null },
    { key: 'standard', icon: BookOpen,    label: t('customerPortal.commitments.tabs.standard'), count: null },
  ];

  if (loading || tab === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  const list = tab === 'pending' ? buckets.pending : tab === 'active' ? buckets.active : tab === 'archive' ? buckets.archive : [];

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('customerPortal.commitments.title')}
        subtitle={t('customerPortal.commitments.subtitle')}
      />

      {/* Tab Bar */}
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line inline-flex gap-1">
        {tabs.map(({ key, icon: Icon, label, count, highlight }) => {
          const active = tab === key;
          const showBadge = count != null && count > 0;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-[8px] text-sm font-medium transition-all ${
                active ? 'bg-white dark:bg-surface-card shadow-sm text-content' : 'text-[var(--text-secondary)] hover:text-content'
              }`}
            >
              <Icon size={15} strokeWidth={1.8} className={highlight && count > 0 ? 'text-amber-500' : ''} />
              <span>{label}</span>
              {showBadge && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  highlight
                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                    : active ? 'bg-[#338edc]/10 text-[#338edc]' : 'bg-[var(--text-tertiary)]/10 text-[var(--text-secondary)]'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'standard' && (
        <StandardTab docs={docs} t={t} />
      )}

      {tab && tab !== 'standard' && (
        list.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {list.map(c => (
              <CommitmentCard key={c.id} c={c} t={t} />
            ))}
          </div>
        ) : (
          <EmptyState tab={tab} t={t} />
        )
      )}
    </div>
  );
}

function CommitmentCard({ c, t }) {
  const validFrom = c.gueltig_ab ? formatDate(c.gueltig_ab) : null;
  const validTo = c.gueltig_bis ? formatDate(c.gueltig_bis) : null;
  const validRange = validFrom || validTo
    ? `${validFrom || '\u2013'} \u2013 ${validTo || '\u221e'}`
    : null;

  return (
    <Link
      to={`/portal/vereinbarungen/${c.id}`}
      className="group bg-surface-card border border-line rounded-card p-5 transition-colors hover:border-[#338edc]/40 block"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-mono text-[var(--text-tertiary)]">{c.commitment_nr}</div>
          <h3 className="text-sm font-semibold text-content mt-0.5 line-clamp-2">{c.titel}</h3>
        </div>
        <StatusBadge status={c.status} t={t} />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[var(--text-secondary)]">
        {c.kategorie && (
          <span className="inline-flex items-center gap-1">
            <span className="text-[var(--text-tertiary)]">{t('customerPortal.commitments.col.kategorie')}:</span>
            <span className="font-medium text-content">{c.kategorie}</span>
          </span>
        )}
        {validRange && (
          <span className="inline-flex items-center gap-1">
            <Clock size={11} strokeWidth={1.8} className="text-[var(--text-tertiary)]" />
            <span>{validRange}</span>
          </span>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-line flex items-center justify-end">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-[#338edc] group-hover:text-[#2570b8]">
          {t('customerPortal.commitments.openCta')}
          <ChevronRight size={13} />
        </span>
      </div>
    </Link>
  );
}

function EmptyState({ tab, t }) {
  const cfg = {
    pending: { Icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10',
      title: t('customerPortal.commitments.empty.pending'),
      hint: t('customerPortal.commitments.emptyHint.pending') },
    active:  { Icon: Inbox, color: 'text-[var(--text-tertiary)]', bg: 'bg-[var(--bg-inset)]',
      title: t('customerPortal.commitments.empty.active'),
      hint: t('customerPortal.commitments.emptyHint.active') },
    archive: { Icon: Archive, color: 'text-[var(--text-tertiary)]', bg: 'bg-[var(--bg-inset)]',
      title: t('customerPortal.commitments.empty.archive'),
      hint: t('customerPortal.commitments.emptyHint.archive') },
  }[tab] || { Icon: Inbox, color: 'text-[var(--text-tertiary)]', bg: 'bg-[var(--bg-inset)]', title: '', hint: '' };
  const { Icon, color, bg, title, hint } = cfg;

  return (
    <div className="bg-surface-card border border-line rounded-card p-10 text-center">
      <div className={`w-12 h-12 rounded-full ${bg} flex items-center justify-center mx-auto mb-3`}>
        <Icon size={22} strokeWidth={1.8} className={color} />
      </div>
      <p className="text-sm font-medium text-content">{title}</p>
      {hint && <p className="text-xs text-[var(--text-tertiary)] mt-1.5">{hint}</p>}
    </div>
  );
}

function StandardTab({ docs, t }) {
  const hasOffer = !!docs.reference_offer_pdf_pfad;
  const hasAgb = !!docs.agb_pdf_pfad;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-blue-800 dark:text-blue-200">
          {t('customerPortal.commitments.standardInfo')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-surface-card border border-line rounded-card p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#338edc]/10 flex items-center justify-center flex-shrink-0">
            <FileText size={18} className="text-[#338edc]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-content text-sm">{t('customerPortal.commitments.referenceOffer')}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
              {docs.reference_offer_version ? `${t('customerPortal.commitments.version')}: ${docs.reference_offer_version}` : t('customerPortal.commitments.noVersionInfo')}
              {docs.reference_offer_datum && ` \u00b7 ${formatDate(docs.reference_offer_datum)}`}
            </div>
            {hasOffer ? (
              <a
                href={`${API}/${docs.reference_offer_pdf_pfad.replace(/^\/+/, '')}`}
                target="_blank" rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#338edc] hover:text-[#2570b8]"
              >
                <Download size={13} />
                {t('customerPortal.commitments.downloadPdf')}
              </a>
            ) : (
              <div className="mt-3 text-xs text-[var(--text-tertiary)] italic">{t('customerPortal.commitments.noPdfYet')}</div>
            )}
          </div>
        </div>

        <div className="bg-surface-card border border-line rounded-card p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#338edc]/10 flex items-center justify-center flex-shrink-0">
            <BookOpen size={18} className="text-[#338edc]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-content text-sm">{t('customerPortal.commitments.agb')}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
              {docs.agb_version_zugestimmt ? `${t('customerPortal.commitments.version')}: ${docs.agb_version_zugestimmt}` : t('customerPortal.commitments.noVersionInfo')}
              {docs.agb_zugestimmt_am && ` \u00b7 ${formatDate(docs.agb_zugestimmt_am)}`}
            </div>
            {hasAgb ? (
              <a
                href={`${API}/${docs.agb_pdf_pfad.replace(/^\/+/, '')}`}
                target="_blank" rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#338edc] hover:text-[#2570b8]"
              >
                <Download size={13} />
                {t('customerPortal.commitments.downloadPdf')}
              </a>
            ) : (
              <div className="mt-3 text-xs text-[var(--text-tertiary)] italic">{t('customerPortal.commitments.noPdfYet')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
